import { computed, reactive, ref, type ComputedRef, type Ref } from 'vue'

import { MAX_NUMERIC, MAX_RANGE_MS, type SearchFilters } from '@/typings/twitter_benchmark_campaign_posts'

const KEYWORD_MAX_LENGTH = 60

const MIN_MAX_KEYS = [
  'winnerCount',
  'followers',
  'impressionsCount',
  'likesCount',
  'retweetsCount',
  'quotesCount',
  'repliesCount',
  'reactionsCount',
  'reactionsRate',
  'campaignDays'
] as const

export type NumericFilterKey = `${(typeof MIN_MAX_KEYS)[number]}${'Min' | 'Max'}`

type FilterInput = {
  industryIds?: number[]
  accountIds?: number[]
  campaignTypes?: number[]
  incentives?: number[]
  tags?: number[]
  isTrend?: boolean
  winnerCountUndetectable?: boolean
} & Partial<Record<NumericFilterKey, number>>

type FilterErrors = Partial<Record<NumericFilterKey | 'keyword' | 'startDatetime' | 'endDatetime', string>>

const RANGE_LABELS: Record<(typeof MIN_MAX_KEYS)[number], string> = {
  winnerCount: '当選人数',
  followers: 'フォロワー数',
  impressionsCount: 'インプレッション数',
  likesCount: 'いいね数',
  retweetsCount: 'リポスト数',
  quotesCount: '引用数',
  repliesCount: '返信数',
  reactionsCount: '反応数',
  reactionsRate: '反応率',
  campaignDays: '開催日数'
}

export type UseTwBenchmarkFilters = {
  keyword: Ref<string>
  startDatetime: Ref<string>
  endDatetime: Ref<string>
  filters: FilterInput
  errors: ComputedRef<FilterErrors>
  isValid: ComputedRef<boolean>
  buildAppliedFilters: () => SearchFilters
  resetFromApplied: (applied?: SearchFilters) => void
}

const REACTIONS_RATE_MAX = 100
const MAX_BY_BASE: Partial<Record<(typeof MIN_MAX_KEYS)[number], number>> = {
  reactionsRate: REACTIONS_RATE_MAX
}

const isValidNumeric = (value: number, max: number): boolean => Number.isFinite(value) && value >= 0 && value <= max

const createInitialFilters = (initial?: SearchFilters): FilterInput => {
  const result: FilterInput = {
    industryIds: initial?.industryIds,
    accountIds: initial?.accountIds,
    campaignTypes: initial?.campaignTypes,
    incentives: initial?.incentives,
    tags: initial?.tags,
    isTrend: initial?.isTrend,
    winnerCountUndetectable: initial?.winnerCountUndetectable
  }
  for (const base of MIN_MAX_KEYS) {
    const minKey = `${base}Min` as NumericFilterKey
    const maxKey = `${base}Max` as NumericFilterKey
    result[minKey] = initial?.[minKey]
    result[maxKey] = initial?.[maxKey]
  }
  return result
}

/**
 * URL クエリから復元した検索条件があれば、リロード時にフォームが空にならないよう初期値として反映する。
 */
export function useTwBenchmarkFilters(initial?: SearchFilters): UseTwBenchmarkFilters {
  const keyword = ref(initial?.keyword ?? '')
  const startDatetime = ref(initial?.startDatetime ?? '')
  const endDatetime = ref(initial?.endDatetime ?? '')
  const filters = reactive<FilterInput>(createInitialFilters(initial))

  const errors = computed<FilterErrors>(() => {
    const next: FilterErrors = {}

    if (keyword.value.length > KEYWORD_MAX_LENGTH) {
      next.keyword = `キーワードは${KEYWORD_MAX_LENGTH}文字以内で指定してください。`
    }

    for (const base of MIN_MAX_KEYS) {
      if (base === 'winnerCount' && filters.winnerCountUndetectable) continue

      const minKey = `${base}Min` as NumericFilterKey
      const maxKey = `${base}Max` as NumericFilterKey
      const minValue = filters[minKey]
      const maxValue = filters[maxKey]
      const max = MAX_BY_BASE[base] ?? MAX_NUMERIC

      if (typeof minValue === 'number' && !isValidNumeric(minValue, max)) {
        next[minKey] = `${RANGE_LABELS[base]}の値が不正です。`
      }
      if (typeof maxValue === 'number' && !isValidNumeric(maxValue, max)) {
        next[maxKey] = `${RANGE_LABELS[base]}の値が不正です。`
      }
      if (typeof minValue === 'number' && typeof maxValue === 'number' && minValue > maxValue) {
        next[minKey] = `${RANGE_LABELS[base]}の値が不正です。`
      }
    }

    if (startDatetime.value === '' || endDatetime.value === '') {
      next.startDatetime = '期間を指定してください。'
    } else {
      const start = new Date(startDatetime.value)
      const end = new Date(endDatetime.value)
      const now = new Date()

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        next.startDatetime = '期間の指定が不正です。'
      } else {
        if (end < start) {
          next.endDatetime = '終了日時は、開始日時より後を指定してください。'
        }
        if (start > now) {
          next.startDatetime = '開始日時に未来の日時は指定できません。'
        }
        if (end > now) {
          next.endDatetime = '終了日時に未来の日時は指定できません。'
        }
        if (end.getTime() - start.getTime() > MAX_RANGE_MS) {
          next.endDatetime = '期間は最大6ヶ月以内で指定してください。'
        }
      }
    }

    return next
  })

  const isValid = computed(() => Object.keys(errors.value).length === 0)

  /**
   * 入力項目の検索履歴が残る場合はリセットさせる
   */
  const resetFromApplied = (applied?: SearchFilters): void => {
    keyword.value = applied?.keyword ?? ''
    Object.assign(filters, createInitialFilters(applied))
  }

  const buildAppliedFilters = (): SearchFilters => {
    const trimmedKeyword = keyword.value.trim()

    const result: SearchFilters = {
      startDatetime: startDatetime.value,
      endDatetime: endDatetime.value,
      keyword: trimmedKeyword === '' ? undefined : trimmedKeyword,
      industryIds: filters.industryIds,
      accountIds: filters.accountIds,
      campaignTypes: filters.campaignTypes,
      incentives: filters.incentives,
      tags: filters.tags,
      isTrend: filters.isTrend,
      winnerCountUndetectable: filters.winnerCountUndetectable
    }

    for (const base of MIN_MAX_KEYS) {
      const minKey = `${base}Min` as NumericFilterKey
      const maxKey = `${base}Max` as NumericFilterKey
      // 当選人数「判別不可のみ」ON 時は Min/Max を送らない（サーバー側でも無視されるが意図を明確にする）
      if (base === 'winnerCount' && filters.winnerCountUndetectable) continue
      result[minKey] = filters[minKey]
      result[maxKey] = filters[maxKey]
    }

    return result
  }

  return {
    keyword,
    startDatetime,
    endDatetime,
    filters,
    errors,
    isValid,
    buildAppliedFilters,
    resetFromApplied
  }
}
